<template>
  <div class="orders-wrapper">
    <header class="page-header fade-in">
      <div>
        <h1>Commandes</h1>
        <p>Suivez vos commandes, leur statut et les montants associés.</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline" @click="refresh" :disabled="loading">
          <i class="bi bi-arrow-clockwise"></i>
          <span>Actualiser</span>
        </button>
        <button class="btn btn-outline" @click="navigateToSales">
          <i class="bi bi-cash-stack"></i>
          <span>Ventes</span>
        </button>
        <button class="btn btn-primary" @click="createOrder">
          <i class="bi bi-plus-circle"></i>
          <span>Nouvelle commande</span>
        </button>
      </div>
    </header>

    <section class="page-body">
      <div class="filters">
        <div class="search">
          <i class="bi bi-search"></i>
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Rechercher (référence, client, statut...)"
          />
        </div>
        <div class="stats" v-if="filteredOrders.length">
          <span>{{ filteredOrders.length }} commande(s)</span>
        </div>
      </div>

      <div v-if="loading" class="state">
        <div class="loader"></div>
        <p>Chargement des commandes…</p>
      </div>

      <div v-else-if="!filteredOrders.length" class="state">
        <i class="bi bi-receipt-cutoff"></i>
        <h3>Aucune commande</h3>
        <p>Ajoutez votre première commande pour commencer le suivi.</p>
        <button class="btn btn-primary" @click="createOrder">
          <i class="bi bi-plus-circle"></i>
          <span>Créer une commande</span>
        </button>
      </div>

      <div v-else class="orders-grid">
        <article
          v-for="order in filteredOrders"
          :key="order.reference || order.id"
          class="order-card fade-in"
          @click="viewOrder(order.reference)"
        >
          <div class="card-top">
            <div class="identity">
              <div class="identity-customer">
                <strong class="customer-name">
                  {{
                    order.customerFullName ||
                    order.customer_name ||
                    order.customer_reference ||
                    "Client inconnu"
                  }}
                </strong>
                <span class="customer-total">
                  {{ formatCurrency(order.totalPriceTTC || order.total) }}
                </span>
              </div>
              <div class="identity-row">
                <!-- <h3>{{ order.reference }}</h3> -->
                <template v-if="order.status">
                  <span
                    class="badge"
                    :class="`badge--${statusClass(order.status)}`"
                  >
                    {{ order.status }}
                  </span>
                </template>
                <template v-else-if="order.validated !== null">
                  <span
                    class="badge"
                    :class="
                      order.validated ? 'badge--completed' : 'badge--pending'
                    "
                  >
                    {{ order.validated ? "Validée" : "En attente" }}
                  </span>
                </template>
              </div>
            </div>
            <div class="meta">
              <span class="meta-item">
                <i class="bi bi-calendar-event"></i>
                {{ formatDate(order.date || order.created_at) }}
              </span>
              <span v-if="order.userName" class="meta-item">
                <i class="bi bi-person-circle"></i>
                {{ order.userName }}
              </span>
            </div>
          </div>

          <div class="card-details">
            <div class="detail">
              <span class="label"
                ><i class="bi bi-currency-exchange"></i>Total</span
              >
              <span class="value">{{
                formatCurrency(order.totalPriceTTC || order.total)
              }}</span>
            </div>
            <div class="detail">
              <span class="label"><i class="bi bi-box-seam"></i>Quantité</span>
              <span class="value">{{
                formatQuantity(order.totalQuantity)
              }}</span>
            </div>
            <div class="detail">
              <span class="label"><i class="bi bi-wallet"></i>Payé</span>
              <span class="value">{{ formatCurrency(order.amountPaid) }}</span>
            </div>
            <div class="detail">
              <span class="label"><i class="bi bi-cash-stack"></i>Restant</span>
              <span class="value">{{
                formatCurrency(order.amountUnpaid)
              }}</span>
            </div>
          </div>

          <div class="card-footer">
            <div class="actions">
              <button
                class="btn btn-outline"
                @click.stop="editOrder(order.reference)"
              >
                <i class="bi bi-pencil"></i>
                <span>Modifier</span>
              </button>
              <button
                class="btn btn-danger"
                @click.stop="deleteOrder(order.reference)"
              >
                <i class="bi bi-trash"></i>
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <button class="fab" @click="createOrder">
      <i class="bi bi-plus-lg"></i>
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "OrdersList",
  data() {
    return {
      loading: false,
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters("sellerOrder", {
      orders: "getAll",
    }),
    normalizedOrders() {
      const toNumber = (value, fallback = 0) => {
        if (value === null || value === undefined || value === "") {
          return fallback;
        }
        const number = Number(value);
        return Number.isNaN(number) ? fallback : number;
      };
      const rawOrders = Array.isArray(this.orders?.data)
        ? this.orders.data
        : this.orders || [];

      return rawOrders.map((entry) => {
        const reference =
          entry.reference || entry.order_reference || entry.id || "";
        return {
          id: entry.id || entry.uuid || reference,
          reference,
          status: entry.status || null,
          validated: entry.validated !== undefined ? entry.validated : null,
          customerFullName:
            entry.customerFullName ||
            entry.customer_full_name ||
            entry.customer?.fullName ||
            entry.customer_name ||
            entry.seller_customer_reference ||
            null,
          customer_reference:
            entry.seller_customer_reference || entry.customer_reference || null,
          date: entry.date || entry.order_date || entry.created_at || null,
          totalPriceTTC: toNumber(
            entry.totalPriceTTC != null
              ? entry.totalPriceTTC
              : entry.total_price_ttc != null
              ? entry.total_price_ttc
              : entry.total_amount != null
              ? entry.total_amount
              : entry.total,
            0
          ),
          totalQuantity: toNumber(
            entry.totalQuantity != null
              ? entry.totalQuantity
              : entry.total_quantity != null
              ? entry.total_quantity
              : entry.items_quantity,
            0
          ),
          amountPaid: toNumber(
            entry.amountPaid != null
              ? entry.amountPaid
              : entry.amount_paid != null
              ? entry.amount_paid
              : entry.paid_amount,
            0
          ),
          amountUnpaid: toNumber(
            entry.amountUnpaid != null
              ? entry.amountUnpaid
              : entry.amount_unpaid != null
              ? entry.amount_unpaid
              : entry.remaining_amount,
            0
          ),
          userName: entry.userName || entry.user_name || null,
          raw: entry,
        };
      });
    },
    filteredOrders() {
      if (!this.searchTerm) {
        return this.normalizedOrders;
      }
      const term = this.searchTerm.toLowerCase();
      return this.normalizedOrders.filter((order) => {
        const values = [
          order.reference,
          order.customerFullName,
          order.customer_reference,
          order.status,
          order.userName,
          order.date,
          order.totalPriceTTC,
          order.totalQuantity,
        ]
          .filter(
            (value) => value !== null && value !== undefined && value !== ""
          )
          .map((value) => value.toString().toLowerCase());
        return values.some((value) => value.includes(term));
      });
    },
  },
  methods: {
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatQuantity(value) {
      if (!value && value !== 0) return "—";
      const number = Number(value);
      if (Number.isNaN(number)) return value;
      return number % 1 === 0 ? number.toString() : number.toFixed(2);
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString();
      } catch (error) {
        return value;
      }
    },
    statusClass(status = "") {
      return status.toLowerCase().replace(/\s+/g, "-");
    },
    async fetchOrders() {
      this.loading = true;
      try {
        await this.$store.dispatch("sellerOrder/getAll");
      } catch (error) {
        console.error("Error loading orders", error);
        this.$toast.error("Impossible de charger les commandes.");
      } finally {
        this.loading = false;
      }
    },
    refresh() {
      this.fetchOrders();
    },
    createOrder() {
      this.$router.push({ name: "orders-create" });
    },
    navigateToSales() {
      this.$router.push({ name: "sales-list" });
    },
    viewOrder(reference) {
      this.$router.push({ name: "orders-show", params: { reference } });
    },
    editOrder(reference) {
      this.$router.push({ name: "orders-edit", params: { reference } });
    },
    async deleteOrder(reference) {
      if (!reference) return;
      const confirmed = await this.$confirm({
        title: "Supprimer la commande",
        message: "Êtes-vous sûr de vouloir supprimer cette commande ?",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (!confirmed) return;
      try {
        await this.$store.dispatch("sellerOrder/destroy", reference);
        this.$toast.success("Commande supprimée avec succès.");
      } catch (error) {
        console.error("Error deleting order", error);
        this.$toast.error("Impossible de supprimer la commande.");
      }
    },
  },
  mounted() {
    if (!this.orders || this.orders.length === 0) {
      this.fetchOrders();
    }
  },
};
</script>

<style scoped>
.orders-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.5rem;
  font-size: 2.25rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
  max-width: 520px;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
}

.search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.2rem;
  border-radius: 16px;
  background: #f3f4f8;
  border: 1px solid #e4e7f1;
  min-width: 280px;
  flex: 1;
}

.search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 1rem;
  width: 100%;
}

.stats {
  color: #6b7280;
  font-weight: 600;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: #6b7280;
  text-align: center;
}

.state i {
  font-size: 3rem;
  color: #2563eb;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.order-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), #ffffff 100%);
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.order-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 46px rgba(15, 23, 42, 0.12);
}

.card-top {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: center;
}

.identity {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.identity-customer {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.customer-name {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  text-transform: uppercase;
}

.customer-total {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2563eb;
}

.meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
  color: #64748b;
  font-size: 0.85rem;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.meta-item i {
  color: #2563eb;
}

.identity-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.identity-row h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #0f172a;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(148, 163, 184, 0.2);
  color: #475569;
}

.badge--pending {
  background: rgba(251, 191, 36, 0.18);
  color: #92400e;
}

.badge--completed {
  background: rgba(34, 197, 94, 0.18);
  color: #166534;
}

.badge--cancelled {
  background: rgba(239, 68, 68, 0.18);
  color: #991b1b;
}

.identity-sub {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.card-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 1rem;
}

.detail {
  background: #f8faff;
  border-radius: 16px;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.label i {
  color: #2563eb;
}

.value {
  color: #0f172a;
  font-weight: 600;
  word-break: break-word;
}

.card-footer {
  margin-top: auto;
}

.actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn i {
  font-size: 1.1rem;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.fab {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #9333ea);
  color: white;
  border: none;
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 30px rgba(79, 70, 229, 0.35);
}

@media (max-width: 1024px) {
  .orders-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
  .page-header {
    flex-direction: column;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .orders-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .search {
    min-width: unset;
  }
  .orders-grid {
    grid-template-columns: 1fr;
  }
  .identity-customer {
    align-items: flex-start;
  }
  .card-top {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }
  .meta {
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .page-body {
    padding: 1.25rem;
  }
  .order-card {
    padding: 1.25rem;
  }
  .btn {
    width: 100%;
    justify-content: center;
  }
  .actions {
    flex-direction: column;
  }
}
</style>
